import { z } from "zod/v4";
import { tripRoleMetadataSchema } from "@/features/trips/role-metadata";

// Wire IDs mirror the backend schema; presentation and available choices come only from metadata.
export const namedSocialIdSchema = z.enum([
  "website",
  "facebook",
  "instagram",
  "tiktok",
  "youtube",
  "linkedin",
  "x",
  "threads",
  "snapchat",
  "reddit",
  "pinterest",
]);
export const socialUrlRulesSchema = z
  .object({
    allowedProtocols: z.array(z.enum(["http:", "https:"])).min(1),
    defaultProtocol: z.enum(["http:", "https:"]),
    allowCredentials: z.literal(false),
  })
  .refine(
    (rules) => rules.allowedProtocols.includes(rules.defaultProtocol),
    "Default protocol must be allowed.",
  );
export type SocialUrlRules = z.infer<typeof socialUrlRulesSchema>;
export const socialMetadataSchema = z.object({
  urlRules: socialUrlRulesSchema,
  options: z
    .array(
      z.object({
        id: namedSocialIdSchema,
        name: z.string().trim().min(1),
        icon: z.string().min(1),
        rank: z.number().int().positive().nullable(),
        placeholder: z.string().min(1),
      }),
    )
    .refine(
      (options) =>
        new Set(options.map((option) => option.id)).size === options.length,
      "Duplicate social option IDs.",
    ),
  custom: z.object({
    id: z.literal("custom"),
    name: z.string().trim().min(1),
    icon: z.string().min(1),
    platformNameRequired: z.literal(true),
    placeholder: z.string().min(1),
  }),
});
export type SocialMetadata = z.infer<typeof socialMetadataSchema>;
export const imageRequirementsSchema = z
  .object({
    format: z.literal("WEBP"),
    mimeType: z.literal("image/webp"),
    maxFileSizeBytes: z.number().int().positive(),
    maxWidthPx: z.number().int().positive(),
    maxHeightPx: z.number().int().positive(),
  })
  .passthrough();
export type ProfileImageRequirements = z.infer<typeof imageRequirementsSchema>;
const section = z.record(z.string(), z.unknown());
// Validate consumed contracts and retain every other section of the complete document.
export const clientMetadataSchema = z
  .object({
    tripRoles: tripRoleMetadataSchema,
    schemaVersion: z.literal(1),
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    countryCatalogRevision: z.string().regex(/^[a-f0-9]{64}$/),
    events: section,
    attachments: section,
    imports: z
      .object({ events: section, trips: section, packingLists: section })
      .passthrough(),
    images: z
      .object({
        tripPosters: section,
        profile: z
          .object({ requirements: imageRequirementsSchema })
          .passthrough(),
      })
      .passthrough(),
    profile: z.object({ socialLinks: socialMetadataSchema }).passthrough(),
    travelResources: z.array(section),
  })
  .passthrough();
export type ClientMetadata = z.infer<typeof clientMetadataSchema>;
export function orderedSocialOptions(metadata: SocialMetadata) {
  return [...metadata.options].sort((a, b) => {
    if (a.rank !== null || b.rank !== null) {
      const rankDifference = (a.rank ?? Infinity) - (b.rank ?? Infinity);
      if (rankDifference) return rankDifference;
    }
    return a.name.localeCompare(b.name);
  });
}
